import type { ProductCollectionItem, ProductCollectionsData } from "./types";

export const MAX_PRODUCT_COLLECTIONS = 3;
export const DEFAULT_MOBILE_DISPLAY_LIMIT = 2;
export const DEFAULT_NUMBER_OF_PRODUCTS = 3;
export const DEFAULT_CTA_LABEL = "See all";

export interface RawProductCollectionItem {
  title: string;
  query: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export interface RawProductCollectionsInput {
  title?: string;
  mobileDisplayLimit?: number;
  numberOfProducts?: number;
  collections?: RawProductCollectionItem[];
}

function normalizeQueryString(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.startsWith("?") ? trimmed : `?${trimmed}`;
}

function normalizeCollectionItem(
  item: RawProductCollectionItem,
): ProductCollectionItem {
  const query = normalizeQueryString(item.query);
  const ctaLabel =
    item.ctaLabel !== undefined && String(item.ctaLabel).trim() !== ""
      ? String(item.ctaLabel).trim()
      : DEFAULT_CTA_LABEL;
  const ctaUrl =
    item.ctaUrl !== undefined && String(item.ctaUrl).trim() !== ""
      ? String(item.ctaUrl).trim()
      : query
        ? `/search${query}`
        : "/search";

  return {
    title: item.title,
    query,
    ctaLabel,
    ctaUrl,
  };
}

export function normalizeProductCollectionsData(
  data: RawProductCollectionsInput,
): ProductCollectionsData {
  const rawCollections = data.collections ?? [];
  const collections = rawCollections
    .map((c) => normalizeCollectionItem(c))
    .slice(0, MAX_PRODUCT_COLLECTIONS);

  const mobileDisplayLimit =
    data.mobileDisplayLimit && data.mobileDisplayLimit > 0
      ? Math.min(data.mobileDisplayLimit, MAX_PRODUCT_COLLECTIONS)
      : DEFAULT_MOBILE_DISPLAY_LIMIT;

  const numberOfProducts =
    data.numberOfProducts && data.numberOfProducts > 0
      ? data.numberOfProducts
      : DEFAULT_NUMBER_OF_PRODUCTS;

  return {
    title: data.title ?? "",
    mobileDisplayLimit,
    numberOfProducts,
    collections,
  };
}
